import React from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useTranslation } from 'react-i18next';
import { Card } from '../../../ComponnentsUI/common/Card';

const NORMAL_COLOR = '#10B981';
const HIGH_COLOR = '#F59E0B';
const LOW_COLOR = '#EF4444';
const FLAGGED_NEUTRAL = '#475569';
const ROUTINE_NEUTRAL = '#CBD5E1';

type Band = 'low' | 'normal' | 'high';

interface LegendRow {
  name: string;
  value: number;
  color: string;
  note?: string;
}

interface HistBucket {
  name: string;
  value: number;
  band: Band;
}

interface FacilityRow {
  name: string;
  flagged: number;
  total: number;
}

const bandColor = (band: Band): string => {
  if (band === 'normal') return NORMAL_COLOR;
  if (band === 'high') return HIGH_COLOR;
  return LOW_COLOR;
};

const SegmentedBar: React.FC<{ rows: LegendRow[] }> = ({ rows }) => {
  const total = rows.reduce((s, r) => s + r.value, 0);
  if (total <= 0) return null;
  const visible = rows.filter((r) => r.value > 0);
  return (
    <div className="w-full flex gap-[2px] h-2">
      {visible.map((r, i) => {
        const isFirst = i === 0;
        const isLast = i === visible.length - 1;
        return (
          <div
            key={r.name}
            className="h-full"
            style={{
              flex: r.value,
              backgroundColor: r.color,
              borderTopLeftRadius: isFirst ? 999 : 0,
              borderBottomLeftRadius: isFirst ? 999 : 0,
              borderTopRightRadius: isLast ? 999 : 0,
              borderBottomRightRadius: isLast ? 999 : 0,
            }}
            title={`${r.name}: ${r.value.toLocaleString()}`}
          />
        );
      })}
    </div>
  );
};

const LegendList: React.FC<{ rows: LegendRow[] }> = ({ rows }) => (
  <div className="mt-3 space-y-2">
    {rows.map((row) => (
      <div
        key={row.name}
        className="flex justify-between items-center text-xs gap-3"
      >
        <div className="flex items-center gap-2 min-w-0">
          <span
            className="w-2.5 h-2.5 rounded-full shrink-0"
            style={{ backgroundColor: row.color }}
          />
          <span className="text-gray-600 dark:text-gray-400 truncate">
            {row.name}
          </span>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <span className="font-semibold text-gray-800 dark:text-gray-200 tabular-nums">
            {row.value.toLocaleString()}
          </span>
          {row.note && (
            <span className="text-gray-500 dark:text-gray-400 tabular-nums w-12 text-right">
              {row.note}
            </span>
          )}
        </div>
      </div>
    ))}
  </div>
);

const BandedHistogram: React.FC<{
  data: HistBucket[];
  unitLabel: string;
}> = ({ data, unitLabel }) => (
  <div className="h-56">
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={data} margin={{ top: 4, right: 18, left: -12, bottom: 0 }}>
        <CartesianGrid
          strokeDasharray="3 3"
          vertical={false}
          stroke="currentColor"
          className="text-gray-200 dark:text-gray-700"
        />
        <XAxis
          dataKey="name"
          tick={{ fontSize: 11 }}
          stroke="currentColor"
          axisLine={false}
          tickLine={false}
          className="text-gray-400"
        />
        <YAxis
          tick={{ fontSize: 11 }}
          stroke="currentColor"
          axisLine={false}
          tickLine={false}
          allowDecimals={false}
          className="text-gray-400"
        />
        <Tooltip
          formatter={(value: number) => [value, unitLabel]}
          cursor={{ fill: 'rgba(148, 163, 184, 0.12)' }}
        />
        <Bar dataKey="value" radius={[4, 4, 0, 0]}>
          {data.map((row, idx) => (
            <Cell key={idx} fill={bandColor(row.band)} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  </div>
);

const HeroStat: React.FC<{
  value: string;
  label: string;
  sub: string;
  range?: string;
}> = ({ value, label, sub, range }) => (
  <div className="mb-4">
    <div className="text-3xl font-bold text-gray-900 dark:text-gray-100 leading-tight">
      {value}
    </div>
    <div className="text-xs font-medium text-gray-600 dark:text-gray-300 mt-0.5">
      {label}
    </div>
    <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">{sub}</div>
    {range && (
      <div className="mt-2 inline-block text-[11px] font-medium px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
        {range}
      </div>
    )}
  </div>
);

const AMNIOTIC_HIST: HistBucket[] = [
  { name: '<20', value: 1, band: 'low' },
  { name: '20-30', value: 2, band: 'normal' },
  { name: '30-40', value: 23, band: 'normal' },
  { name: '40-50', value: 38, band: 'normal' },
  { name: '50-60', value: 46, band: 'normal' },
  { name: '60-70', value: 41, band: 'normal' },
  { name: '70-80', value: 19, band: 'normal' },
  { name: '80+', value: 8, band: 'high' },
];

const FHR_HIST: HistBucket[] = [
  { name: '<110', value: 0, band: 'low' },
  { name: '110-130', value: 4, band: 'normal' },
  { name: '130-140', value: 47, band: 'normal' },
  { name: '140-150', value: 59, band: 'normal' },
  { name: '150-160', value: 51, band: 'normal' },
  { name: '>160', value: 12, band: 'high' },
];

const PREECLAMPSIA_BY_FACILITY: FacilityRow[] = [
  { name: 'CSR2 Aghbala', flagged: 16, total: 69 },
  { name: 'CSU El Ksiba', flagged: 13, total: 47 },
  { name: 'CSR2 Moulay Bouazza', flagged: 12, total: 42 },
  { name: 'CSR2 Anergui', flagged: 3, total: 12 },
  { name: 'CSR2 Aït Tamlil', flagged: 1, total: 7 },
  { name: 'CSR1 Boutferda', flagged: 0, total: 10 },
];

export const AmnioticFluidCard: React.FC = () => {
  const { t } = useTranslation('Dashboard');
  const legend: LegendRow[] = [
    {
      name: t('kpis.clinical.amniotic.normalLabel'),
      value: 169,
      color: NORMAL_COLOR,
      note: '94.9%',
    },
    {
      name: t('kpis.clinical.amniotic.polyLabel'),
      value: 8,
      color: HIGH_COLOR,
      note: '4.5%',
    },
    {
      name: t('kpis.clinical.amniotic.oligoLabel'),
      value: 1,
      color: LOW_COLOR,
      note: '0.6%',
    },
  ];
  return (
    <Card title={t('kpis.cards.amnioticFluid')}>
      <HeroStat
        value={t('kpis.clinical.amniotic.mean')}
        label={t('kpis.clinical.amniotic.heroLabel')}
        sub={t('kpis.clinical.amniotic.median')}
        range={t('kpis.clinical.amniotic.normalRange')}
      />
      <SegmentedBar rows={legend} />
      <LegendList rows={legend} />
      <div className="mt-6">
        <BandedHistogram
          data={AMNIOTIC_HIST}
          unitLabel={t('kpis.clinical.amniotic.exams')}
        />
      </div>
    </Card>
  );
};

export const FetalHeartRateCard: React.FC = () => {
  const { t } = useTranslation('Dashboard');
  const legend: LegendRow[] = [
    {
      name: t('kpis.clinical.fhr.normalLabel'),
      value: 161,
      color: NORMAL_COLOR,
      note: '93.1%',
    },
    {
      name: t('kpis.clinical.fhr.tachyLabel'),
      value: 12,
      color: HIGH_COLOR,
      note: '6.9%',
    },
    {
      name: t('kpis.clinical.fhr.bradyLabel'),
      value: 0,
      color: LOW_COLOR,
      note: '0%',
    },
  ];
  return (
    <Card title={t('kpis.cards.fetalHeartRate')}>
      <HeroStat
        value={t('kpis.clinical.fhr.mean')}
        label={t('kpis.clinical.fhr.heroLabel')}
        sub={t('kpis.clinical.fhr.median')}
        range={t('kpis.clinical.fhr.normalRange')}
      />
      <SegmentedBar rows={legend} />
      <LegendList rows={legend} />
      <div className="mt-6">
        <BandedHistogram
          data={FHR_HIST}
          unitLabel={t('kpis.clinical.fhr.exams')}
        />
      </div>
    </Card>
  );
};

const FacilityFlaggedBars: React.FC<{
  data: FacilityRow[];
  countLabel: string;
  totalLabel: string;
}> = ({ data, countLabel, totalLabel }) => {
  const maxTotal = Math.max(...data.map((d) => d.total));
  return (
    <div style={{ height: Math.max(40 * data.length + 20, 200) }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={data}
          layout="vertical"
          margin={{ top: 4, right: 60, left: 8, bottom: 4 }}
        >
          <CartesianGrid
            strokeDasharray="3 3"
            horizontal
            vertical={false}
            stroke="currentColor"
            className="text-gray-200 dark:text-gray-700"
          />
          <XAxis
            type="number"
            domain={[0, maxTotal]}
            tick={{ fontSize: 11 }}
            stroke="currentColor"
            axisLine={false}
            tickLine={false}
            allowDecimals={false}
            className="text-gray-400"
          />
          <YAxis
            type="category"
            dataKey="name"
            tick={{ fontSize: 11 }}
            stroke="currentColor"
            width={148}
            axisLine={false}
            tickLine={false}
            className="text-gray-500 dark:text-gray-400"
          />
          <Tooltip
            cursor={{ fill: 'rgba(148, 163, 184, 0.12)' }}
            formatter={(_v, _n, item: any) => {
              const p = item?.payload as FacilityRow | undefined;
              if (!p) return [_v, _n];
              return [`${p.flagged} / ${p.total}`, `${countLabel} / ${totalLabel}`];
            }}
          />
          <Bar dataKey="flagged" radius={[0, 4, 4, 0]} fill={FLAGGED_NEUTRAL}>
            <LabelList
              dataKey="flagged"
              position="right"
              content={(props: any) => {
                const { x, y, width, height, value, index } = props;
                const row = data[index];
                if (!row) return null;
                return (
                  <text
                    x={(x ?? 0) + (width ?? 0) + 6}
                    y={(y ?? 0) + (height ?? 0) / 2}
                    dominantBaseline="middle"
                    className="text-[11px] fill-gray-700 dark:fill-gray-200 tabular-nums"
                  >
                    {value} / {row.total}
                  </text>
                );
              }}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export const PreEclampsiaScreeningCard: React.FC = () => {
  const { t } = useTranslation('Dashboard');
  const legend: LegendRow[] = [
    {
      name: t('kpis.clinical.preeclampsia.flagged'),
      value: 45,
      color: FLAGGED_NEUTRAL,
    },
    {
      name: t('kpis.clinical.preeclampsia.routine'),
      value: 141,
      color: ROUTINE_NEUTRAL,
    },
  ];
  return (
    <Card title={t('kpis.cards.preeclampsiaScreening')}>
      <HeroStat
        value="45"
        label={t('kpis.clinical.preeclampsia.heroLabel')}
        sub={t('kpis.clinical.preeclampsia.heroSub')}
      />
      <SegmentedBar rows={legend} />
      <LegendList rows={legend} />
      <div className="mt-6">
        <div className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-2">
          {t('kpis.clinical.preeclampsia.byFacility')}
        </div>
        <FacilityFlaggedBars
          data={PREECLAMPSIA_BY_FACILITY}
          countLabel={t('kpis.clinical.preeclampsia.flagged')}
          totalLabel={t('kpis.clinical.preeclampsia.total')}
        />
      </div>
    </Card>
  );
};
